/**
 * Tenants Management Page
 * Platform admin page for managing customer tenants
 */
import React from 'react';
import {
  Title1,
  Body1,
  Card,
  CardHeader,
  Button,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import {
  BuildingRegular,
  AddRegular,
  PersonRegular,
  CalendarRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { tenantsApi } from '../services/api';
import type { TenantSummary, TenantStatus, SubscriptionTier } from '../types';

const statusColors: Record<TenantStatus, 'success' | 'warning' | 'danger' | 'informative'> = {
  Active: 'success',
  Pending: 'informative',
  Suspended: 'warning',
  Deactivated: 'danger',
};

const tierColors: Record<SubscriptionTier, 'subtle' | 'brand' | 'success' | 'danger'> = {
  Free: 'subtle',
  Starter: 'brand',
  Professional: 'success',
  Enterprise: 'danger',
};

export const TenantsPage: React.FC = () => {
  const {
    data: tenants,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantsApi.getAll(true),
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spinner size="large" label="Loading tenants..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card style={{ margin: '1rem', padding: '1rem' }}>
        <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>
          Error loading tenants: {error instanceof Error ? error.message : 'Unknown error'}
        </Body1>
      </Card>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <BuildingRegular style={{ fontSize: '2rem' }} />
          <Title1>Customer Tenants</Title1>
        </div>
        <Button appearance="primary" icon={<AddRegular />}>
          Create Tenant
        </Button>
      </div>

      <Card>
        <CardHeader
          header={<Body1 style={{ fontWeight: 600 }}>All Tenants ({tenants?.length ?? 0})</Body1>}
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Tenant Name</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Subscription</TableHeaderCell>
              <TableHeaderCell>Users</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell>Last Activity</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants?.map((tenant: TenantSummary) => (
              <TableRow key={tenant.tenantId}>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <BuildingRegular />
                    <span style={{ fontWeight: 500 }}>{tenant.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge appearance="filled" color={statusColors[tenant.status]}>
                    {tenant.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline" color={tierColors[tenant.subscriptionTier]}>
                    {tenant.subscriptionTier}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <PersonRegular />
                    {tenant.userCount}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <CalendarRegular />
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell>
                  {tenant.lastActivityAt
                    ? new Date(tenant.lastActivityAt).toLocaleDateString()
                    : 'Never'}
                </TableCell>
              </TableRow>
            ))}
            {(!tenants || tenants.length === 0) && (
              <TableRow>
                <TableCell colSpan={6}>
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Body1>No tenants found</Body1>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
